import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getCoreRowModel } from '@tanstack/react-table';
import { isEqual } from 'lodash';

// MUI Theme
import { Button, MenuItem, Paper, Select } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import useConfirmDialog from '../../../hooks/useConfirmDialog';

// Services
import { addGeofences, deleteGeofence, updateGeofences } from '../../../services/geofence.service';
import {
  getFacilities,
  addFacility,
  updateFacility,
  deleteFacility,
  getFacilityById,
  exportFacilities,
  getFacilityChangeLog,
  addCarriers,
  deleteCarriers,
} from '../../../services/facility.service';

// Utils
import { makeFacilityListColumn } from '../../../utils/columns/makeColumn';
import { Storage, getRetainState } from '../../../utils/Storage';
import { calculateCentroid } from '../../../utils/mapHelper';
import { useExport } from '../../../hooks/useExport';

// Types
import { CONFIRM_DIALOG_CANCEL, SearchBarFilterTypes } from '../../../constants/Constants';
import { FilterableTypes } from '../../../types/FilterableTypes';
import { Facility } from '../../../types/Facility';
import { GeoPosition } from '../../../types/GeoPosition';
import { Geofence } from '../../../types/Geofence';

// Redux
import { fetchAllSettings } from '../../../redux/slices/settingsSlice';
import { updateTargetLocationState } from '../../../redux/slices/mapSlice';

// Common Components
import { useStandardTable as useTable, Table } from '../../common/table/index';
import { AppPermissions } from '../../../constants/Permissions';
import { AuthGuard } from '../../common/AuthGuard';
import ChangeLogList from '../../common/ChangeLog';
import SearchBar from '../../common/SearchBar/SearchBar';
import CustomModal from '../../common/withModal';
import withForm from '../../common/withForm';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

// Components
import { PaginatedRequest } from '../../../requestModels/paginatedRequest';
import facilityForm, { FacilityInput } from './facilityForm';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';
import { AddCarrierPayload } from '../../../types/Carrier';

const FacilityForm = withForm(facilityForm);

interface FacilityProp {
  accessKey?: string;
  handleCarrierPayload?: (data: AddCarrierPayload) => void;
  defaultCarriers?: any;
}

export default function Facilities(props: FacilityProp): JSX.Element {
  const dispatch = useAppDispatch();
  const settingsState = useAppSelector((state) => state.settings);
  const { notifyMessage } = useNotifyMessage();
  const [changeLogTargetId, setChangeLogTargetId] = useState<string | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const forceReload = () => setParamFilter({ ...paramFilter, changedAt: new Date() });
  const [facilityType, setFacilityType] = useState<number | undefined>();
  const [paramFilter, setParamFilter] = useState<any>({
    entityType: 'Facility',
    filters: [],
    sortBy: [{ name: '1' }],
  });

  const [carrierPayload, setCarrierPayload] = useState<AddCarrierPayload>({ carrierIds: [] });

  const { renderDialog, confirm } = useConfirmDialog(CONFIRM_DIALOG_CANCEL);

  const originalCarrierIdsRef = useRef<string[]>([]);

  const { isExporting, startExport } = useExport('Facility');

  const normalizeCarrierIds = (carrierIds: any[]): string[] => {
    if (!Array.isArray(carrierIds)) return [];
    return carrierIds
      .map((c) => {
        if (!c) return '';
        if (typeof c === 'string') return c;
        // common shapes: { objectId }, { id }, or maybe { carrierId }
        return c.objectId ?? c.id ?? c.carrierId ?? '';
      })
      .filter(Boolean);
  };

  const intialDefaultFilters = () => {
    let filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.FACILITY_TMP, true);
    if (!filterState) {
      filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.FACILITY);
      getRetainState(SearchBarFilterTypes.FACILITY, 'facilityType', setFacilityType);
    }

    if (filterState) {
      setParamFilter({
        entityType: 'Facility',
        filters: filterState.filters,
        sortBy: paramFilter.sortBy,
      });
    }
  };

  useEffect(() => {
    dispatch(fetchAllSettings());
    intialDefaultFilters();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      // avoid creating new object if already empty
      setCarrierPayload((prev) => (prev?.carrierIds?.length === 0 ? prev : { carrierIds: [] }));
    }
  }, [isOpen]);

  const memoDefaultCarriers = useMemo(() => carrierPayload, [carrierPayload]);

  const getChangeLog = async (request: PaginatedRequest): Promise<any> => {
    if (changeLogTargetId) return await getFacilityChangeLog(changeLogTargetId, request);

    return null;
  };

  const createGeofenceRequest = (data: Facility, geofences: Geofence[]) => {
    const createGofence = geofences[0];
    const { id, stationIataCode, stationId, name } = data;
    if (!id) return null;

    createGofence.facilityId = id;
    createGofence.facilityName = name;
    createGofence.stationIataCode = stationIataCode;
    createGofence.stationId = stationId;
    createGofence.name = `${stationIataCode} - ${name}`;
    createGofence.type = 1; // GEOFENCE_TYPE_CIRCULAR = 0,  GEOFENCE_TYPE_POLYGON = 1
    createGofence.level = 1; // GEOFENCE_LEVEL_STATION = 0, GEOFENCE_LEVEL_FACILITY = 1
    createGofence.typeDescr = 'Polygon';

    const centroidMap = calculateCentroid(createGofence?.coordinates ?? []);
    if (centroidMap) {
      createGofence.location = {
        type: 'Point',
        latitude: centroidMap?.lat,
        longitude: centroidMap?.lng,
      } as GeoPosition;
    }

    return createGofence;
  };

  const handleCarrierPayload = useCallback((carriers: AddCarrierPayload) => {
    setCarrierPayload((prev) => (isEqual(prev, carriers) ? prev : carriers));
  }, []);

  const formik = useFormik<FacilityInput>({
    initialValues: {
      id: '',
      name: '',
      stationIataCode: '',
      stationId: undefined,
      facilityType: undefined,
      latitude: '',
      longitude: '',
    },
    validationSchema: () => {
      return Yup.object<FacilityInput>({
        name: Yup.string().required('This field is required+'),
        stationIataCode: Yup.string().required('This field is required'),
        latitude: Yup.string().required('This field is required'),
        longitude: Yup.string().required('This field is required'),
        facilityType: Yup.number().notRequired(),
      });
    },
    enableReinitialize: true, // Enable reinitialization
    validateOnBlur: true,
    onSubmit: async (values) => {
      const { id, name, stationIataCode, stationId, facilityType, latitude, longitude, geofences } = values;

      const payload: Facility = {
        id,
        name,
        stationIataCode,
        stationId,
        facilityType,
        location: {
          type: 'Point',
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
        },
      } as Facility;

      // helper: normalize current carrier ids to string[]
      const normalizeCarrierIdsLocal = (carrierIds: any[] = []) => {
        if (!Array.isArray(carrierIds)) return [];
        return carrierIds
          .map((c) => {
            if (!c) return '';
            if (typeof c === 'string') return c;
            return c.objectId ?? c.id ?? c.carrierId ?? '';
          })
          .filter(Boolean);
      };

      if (id) {
        let facilityUpdated = false;

        await updateFacility(payload)
          .then(async (res: any) => {
            facilityUpdated = true;

            if (geofences && geofences?.length > 0) {
              const updateGofence = geofences[0];
              if (updateGofence.coordinates && updateGofence.coordinates.length > 0) {
                if (updateGofence.id) {
                  await updateGeofences(updateGofence).catch(() => {
                    notifyMessage('Unable to update Geofence', 'error');
                  });
                } else {
                  const createGofence = createGeofenceRequest(res.data, geofences);
                  if (createGofence) {
                    await addGeofences({ ...createGofence }).catch(() => {
                      notifyMessage('Unable to create Geofence', 'error');
                    });
                  }
                }
              } else {
                if (updateGofence.id && updateGofence.coordinates?.length === 0) {
                  await deleteGeofence(updateGofence.id).catch(() => {
                    notifyMessage('Unable to delete Geofence', 'error');
                  });
                }
              }
            }

            notifyMessage('Update Facility success!', 'success');
          })
          .catch(() => {
            notifyMessage('Unable to update facility', 'error');
          });

        const currentIds = normalizeCarrierIdsLocal(carrierPayload?.carrierIds ?? []);
        const originalIds = originalCarrierIdsRef.current ?? [];

        const added = currentIds.filter((i) => !originalIds.includes(i));
        const removed = originalIds.filter((i) => !currentIds.includes(i));

        // Only call addCarriers if there's something to add
        if (added.length > 0) {
          try {
            const payloadForAdd: AddCarrierPayload = {
              carrierIds: added.map((aid) => ({ objectId: aid, isLocked: true })),
            };
            await addCarriers(id, payloadForAdd);
            notifyMessage('Carriers added successfully!', 'success');
          } catch (err) {
            console.error('addCarriers failed', err);
            notifyMessage('Unable to add carriers', 'error');
          }
        }

        // Only call deleteCarriers if there's something to remove
        if (removed.length > 0) {
          try {
            await deleteCarriers(id, { carrierIds: removed });
            notifyMessage('Carriers removed successfully!', 'success');

            // update original ref so future diffs don't try to re-add these
            originalCarrierIdsRef.current = originalCarrierIdsRef.current.filter((x) => !removed.includes(x));
          } catch (err) {
            console.error('deleteCarriers failed', err);
            notifyMessage('Unable to remove carriers', 'error');
          }
        }

        if (facilityUpdated || added.length > 0 || removed.length > 0) {
          setIsOpen(false);
          forceReload();
        }
      } else {
        await addFacility(payload)
          .then(async (res: any) => {
            if (geofences && geofences?.length > 0 && res.data) {
              const createGofence = createGeofenceRequest(res.data, geofences);
              if (createGofence) {
                await addGeofences({ ...createGofence }).catch(() => {
                  notifyMessage('Unable to create Geofence', 'error');
                });
              }
            }

            // For create: add all current carriers (normalize and map)
            const normalized = normalizeCarrierIdsLocal(carrierPayload?.carrierIds ?? []);
            if (res?.data?.id && normalized.length > 0) {
              try {
                const payloadForAdd: AddCarrierPayload = {
                  carrierIds: normalized.map((cid) => ({ objectId: cid, isLocked: true })),
                };
                await addCarriers(res.data.id, payloadForAdd);
                notifyMessage('Carriers added successfully!', 'success');
              } catch {
                notifyMessage('Unable to add carriers', 'error');
              }
            }

            notifyMessage('Create Facility success!', 'success');
            setIsOpen(false);
            forceReload();
          })
          .catch(() => {
            notifyMessage('Unable to create facility', 'error');
          });
      }
    },
  });

  const handleEdit = async (facility: Facility) => {
    dispatch(updateTargetLocationState(null));
    await getFacilityById(facility.id).then((res: any) => {
      const { id, name, stationIataCode, stationId, facilityType, geofences } = res.data;
      formik.setValues({
        id,
        name,
        stationIataCode,
        stationId,
        facilityType,
        latitude: res.data?.location?.latitude.toString(),
        longitude: res.data?.location?.longitude.toString(),
        geofences,
      });

      // pre-populate carriers and store normalized original ids
      const incoming = { carrierIds: res.data?.carrierIds ?? [] };
      const normalizedOriginals = normalizeCarrierIds(incoming.carrierIds);
      originalCarrierIdsRef.current = normalizedOriginals;

      setCarrierPayload((prev) => (isEqual(prev, incoming) ? prev : incoming));
      setIsOpen(true);
    });
  };

  const handleDelete = useCallback(
    async (id: string) => {
      const { accepted } = await confirm({});
      if (!accepted) return;
      await deleteFacility(id)
        .then(() => {
          notifyMessage('Facility has been removed', 'success');
          forceReload();
        })
        .catch(() => {
          notifyMessage('Unable to delete facility', 'error');
        });
    },
    [confirm],
  );

  const handleOpenChangeLog = (id: string): void => {
    dispatch(updateTargetLocationState(null));
    setChangeLogTargetId(id);
  };

  const columns = useMemo(() => makeFacilityListColumn(handleDelete, handleEdit, handleOpenChangeLog), []);
  const table = useTable<any>({
    columns,
    accessKey: props.accessKey,
    fetchAsync: getFacilities,
    exportAsync: exportFacilities,
    params: paramFilter,
    serverSide: true,
  });

  return (
    <React.Fragment>
      <AuthGuard permission={AppPermissions.Facility.Edit}>
        {renderDialog()}
        <div style={{ height: '30px', position: 'relative' }}>
          <div style={{ position: 'absolute', top: -14, right: 0 }}>
            <Button
              onClick={() => {
                formik.resetForm();
                formik.setValues({
                  id: '',
                  name: '',
                  stationIataCode: '',
                  stationId: undefined,
                  facilityType: undefined,
                  latitude: '0', //TODO : have to get from station location
                  longitude: '0', //TODO : have to get from station location
                  geofences: undefined,
                });
                setIsOpen(true);
              }}
              variant='contained'
              size='small'
              sx={tablePrimaryButtonSx}
              endIcon={<AddCircleOutlineOutlinedIcon style={{ fontSize: 20 }} />}
            >
              Add Facility
            </Button>
          </div>
        </div>
        <CustomModal title='Facility' isOpen={isOpen} isValid={formik.isValid} setIsOpen={setIsOpen} onConfirm={formik.handleSubmit} maxWidth='lg'>
          <FacilityForm formRef={formik} handleCarrierPayload={handleCarrierPayload} defaultCarriers={memoDefaultCarriers} />
        </CustomModal>
      </AuthGuard>
      <CustomModal title='ChangeLog' isOpen={changeLogTargetId !== undefined} setIsOpen={() => setChangeLogTargetId(undefined)} maxWidth='lg'>
        <ChangeLogList fetchAsync={getChangeLog} />
      </CustomModal>

      <SearchBar
        pageKey={SearchBarFilterTypes.FACILITY}
        onSearch={(filters: any) => {
          const finalFilters = [...filters];
          if (facilityType !== undefined) {
            finalFilters.push({
              facilityType: facilityType,
              filterType: FilterableTypes.Equals,
            });
          }
          setParamFilter({ entityType: 'Facility', filters: finalFilters, sortBy: paramFilter.sortBy });
          Storage.setSearchBarFiltering(SearchBarFilterTypes.FACILITY, { isMoreFilter: true, filters: finalFilters });
        }}
        onReset={() => {
          setFacilityType(undefined);
          setParamFilter({ entityType: 'Facility', filters: [], sortBy: paramFilter.sortBy });
        }}
        allowSearchAll={true}
        columns={columns}
        components={
          <div style={{ display: 'flex', marginBottom: 5 }}>
            <FormInputCollection label='Facility Type' style={{ width: '140px' }}>
              <Select
                value={facilityType ?? 'all'}
                variant='outlined'
                id='facilityType'
                name='status'
                size='small'
                style={{ width: '140px' }}
                onChange={(e: any) => {
                  setFacilityType(e.target.value === 'all' ? undefined : e.target.value);
                }}
              >
                <MenuItem value={'all'}>All</MenuItem>
                {settingsState.facilityTypes?.value.map((val, index: number) => {
                  return (
                    <MenuItem key={index} value={val.text}>
                      {val.value}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormInputCollection>
          </div>
        }
      />
      <Paper>
        <Table
          {...table}
          isExporting={isExporting}
          canExportData={() => true}
          exportData={() => startExport(paramFilter.filters ?? [])}
          showSearchBar={false}
          showColumnToggler={false}
          showAction={true}
          getCoreRowModel={getCoreRowModel()}
        />
      </Paper>
    </React.Fragment>
  );
}
